import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Flex, Loader, Modal, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { InstagramComment } from '../../../../types/instagram-comments';

import { CommentCard } from './CommentCard';

import { axios } from '@/libraries/axios';

type Props = {
  pageId: string;
  pageName: string;
  mediaId?: string;
  mediaType?: string;
};

const fetchComments = async (pageId: string, mediaId?: string): Promise<InstagramComment[]> => {
  const response: any = await axios.get('/meta/comments', {
    params: { pageId, ...(mediaId && { mediaId }) },
  });
  return response.comments || [];
};

const mediaLabels: Record<string, string> = {
  REEL: 'Reels',
  STORY: 'Stories',
  FEED: 'Post',
};

export const CommentsModal = NiceModal.create(({ pageId, pageName, mediaId, mediaType }: Props) => {
  const modal = useModal();

  const { data: comments, isLoading } = useQuery({
    queryKey: ['instagram-comments', pageId, mediaId],
    queryFn: () => fetchComments(pageId, mediaId),
    enabled: modal.visible,
  });

  const title = mediaId ? (
    <Title order={3}>
      Comments on {(mediaType && mediaLabels[mediaType]) || 'Post'} · {pageName}
    </Title>
  ) : (
    <Title order={3}>All comments Instagram: {pageName}</Title>
  );

  return (
    <Modal
      size="xl"
      centered
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={() => modal.remove()}
      title={title}
    >
      <ScrollArea h={600}>
        {isLoading ? (
          <Flex justify="center" align="center" h={200}>
            <Loader size="lg" />
          </Flex>
        ) : comments && comments.length > 0 ? (
          <Stack gap="md">
            {comments.map((comment: InstagramComment) => (
              <CommentCard key={comment._id} comment={comment} />
            ))}
          </Stack>
        ) : (
          <Flex justify="center" align="center" h={200}>
            <Text c="dimmed" size="lg">
              Comments are absent
            </Text>
          </Flex>
        )}
      </ScrollArea>
    </Modal>
  );
});
